import React, { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Link,
  Box,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import { ArrowRight } from "lucide-react";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const ResendConfirmation = ({ onSectionClicked }) => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();

  const initialFormValues = {
    email: "",
  };

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );

    if ("email" in fieldValues) {
      temp.email = fieldValues.email ? "" : required;
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();
    if (validate(values)) {
      sh.post(`/${i18nLocale}/candidates/confirmation`, {
        authenticity_token: authenticityToken,
        candidate: {
          email: values.email,
        },
      })
        .then(() => {
          setIsSuccess(true);
        })
        .catch(() => {
          enqueueSnackbar(
            intl.formatMessage({
              id: "flash.unknown",
            }),
            { variant: "error" }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <form onSubmit={handleSubmit}>
          <Box>
            <Box>
              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12}>
                  <Box sx={{ borderBottom: "1px solid hsl(220, 13%, 91%)" }} />
                </Grid>
                {!isSuccess ? (
                  <>
                    <Grid item xs={12}>
                      <Box
                        component="p"
                        sx={{
                          m: 0,
                          fontSize: "0.875rem",
                          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                          color: "hsl(220, 10%, 46%)",
                          whiteSpace: "break-spaces",
                        }}
                      >
                        <FormattedMessage id="devise.confirmation.confirmation_instructions" />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        type="email"
                        margin="normal"
                        variant="outlined"
                        onBlur={handleInputValue}
                        onChange={handleInputValue}
                        name="email"
                        sx={inputSx}
                        {...(errors["email"] && {
                          error: true,
                          helperText: errors["email"],
                        })}
                        label={<FormattedMessage id="devise.signup.email" />}
                      />
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        fontSize: "0.875rem",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        color: "hsl(153 68% 25%)",
                      }}
                    >
                      <FormattedMessage id="devise.confirmation.confirmation_success" />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ mt: "16px" }}>
                  <Button
                    fullWidth
                    type="submit"
                    disabled={loading || isSuccess}
                    sx={{
                      bgcolor: "hsl(153 68% 25%)",
                      color: "white",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      px: "1.5rem",
                      py: "0.75rem",
                      borderRadius: "0.75rem",
                      textTransform: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      "&:hover": {
                        bgcolor: "hsl(153 68% 25% / 0.9)",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "hsl(153 68% 25% / 0.3)",
                        color: "hsl(0 0% 100% / 0.6)",
                      },
                    }}
                  >
                    <Box justifyContent="center">
                      {loading && (
                        <CircularProgress size={16} sx={{ color: "white" }}>
                          <FormattedMessage id="send" />
                        </CircularProgress>
                      )}
                      {!loading && <FormattedMessage id="send" />}
                    </Box>
                    {!loading && <ArrowRight size={14} />}
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2, textAlign: "center" }}>
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.875rem",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      <Link
                        onClick={() => onSectionClicked("login")}
                        underline="always"
                        sx={{
                          cursor: "pointer",
                          color: "hsl(153 68% 25%)",
                          textDecorationColor: "hsl(153 68% 25% / 0.4)",
                          "&:hover": {
                            textDecorationColor: "hsl(153 68% 25%)",
                          },
                        }}
                      >
                        <FormattedMessage id="devise.password.back_to_login" />
                      </Link>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </form>
      </Grid>
    </Grid>
  );
};

export default ResendConfirmation;
